import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';

import { GradeLesson } from '../../models/grade-lesson.school.model';
import * as gradeLessonActions from '../actions/grade-lesson.school.actions';

export interface GradeLessonState extends EntityState<GradeLesson> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  gradeLessonStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const gradeLessonAdapter: EntityAdapter<GradeLesson> =
  createEntityAdapter<GradeLesson>();

export const initialState: GradeLessonState =
  gradeLessonAdapter.getInitialState({
    loading: false,
    loaded: false,
    inProgress: false,
    gradeLessonStatsLoading: false,
    error: undefined,
  });

export const gradeLessonReducer = createReducer(
  initialState,
  on(gradeLessonActions.getGradeLessons, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(gradeLessonActions.getGradeLesson, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(gradeLessonActions.addGradeLessonToStore, (state, { lesson }) =>
    gradeLessonAdapter.upsertOne(lesson, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(gradeLessonActions.addGradeLessonsToStore, (state, { lessons }) => {
    return gradeLessonAdapter.upsertMany(lessons, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    });
  }),
  on(gradeLessonActions.getGradeLessonsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(gradeLessonActions.getGradeLessonFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(gradeLessonActions.create, (state, { gradeLesson }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeLessonActions.createSuccess, (state, { lesson }) =>
    gradeLessonAdapter.upsertOne(lesson, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeLessonActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeLessonActions.update, (state, { gradeLesson }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeLessonActions.updateSuccess, (state, { lesson }) =>
    gradeLessonAdapter.upsertOne(lesson, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeLessonActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeLessonActions.updateNotes, (state) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeLessonActions.updateNotesSuccess, (state, { lesson }) =>
    gradeLessonAdapter.upsertOne(lesson, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeLessonActions.updateNotesFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeLessonActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeLessonActions.deleteSuccess, (state, { id }) =>
    gradeLessonAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeLessonActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeLessonActions.clearFromStore, (state, { ids }) =>
    gradeLessonAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeLessonActions.clear, (state) => initialState),
);
